import React from 'react';
import { Calendar, Star, MessageSquare, Clock, Users, BarChart3, Zap, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';

export const FeaturesSection = () => {
  const features = [
    {
      icon: Calendar,
      title: "Intelligent Appointment Scheduling",
      description: "AI-powered scheduling that understands patient care plans, follow-up sequences, and recovery patterns. Reduces no-shows by 40% across all healthcare specialties.",
      highlight: "24/7 Availability"
    },
    {
      icon: Star,
      title: "Automated Review Collection",
      description: "Intelligent review requests sent at optimal times after successful treatments. Boost your practice rating and attract more patients effortlessly.",
      highlight: "85% Response Rate"
    },
    {
      icon: MessageSquare,
      title: "Multi-Channel Patient Communication",
      description: "Engage patients through SMS, email, and voice calls with personalized messaging about care plans, treatment progress, and aftercare instructions.",
      highlight: "14 Languages"
    },
    {
      icon: Clock,
      title: "24/7 Patient Support",
      description: "Answer common questions about treatments, pricing, insurance, and post-treatment care around the clock without human intervention.",
      highlight: "90% Resolution"
    },
    {
      icon: Users,
      title: "Multi-Location Management",
      description: "Manage multiple healthcare locations and practitioners from a single dashboard. Perfect for growing healthcare organizations.",
      highlight: "Unlimited Locations"
    },
    {
      icon: BarChart3,
      title: "Patient Care Analytics",
      description: "Deep insights into patient satisfaction, treatment outcomes, appointment patterns, and recovery progress. Make data-driven care decisions.",
      highlight: "Real-time Data"
    },
    {
      icon: Zap,
      title: "Quick Integration",
      description: "Seamlessly connects with popular practice management systems like Epic, Cerner, Athenahealth, and more. No disruption to your workflow.",
      highlight: "3-Day Setup"
    },
    {
      icon: Shield,
      title: "HIPAA Compliant",
      description: "Enterprise-grade security and privacy protection for all patient health information. Your patient data is encrypted and protected at all times.",
      highlight: "100% Secure"
    }
  ];

  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-6 mb-20">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Powerful Features for
            <span className="text-emerald-700">
              {" "}Healthcare Practices
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to automate patient management, streamline aftercare processes, 
            and grow your healthcare practice with AI-powered efficiency.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="group hover:shadow-lg transition-all duration-300 border border-emerald-200 bg-white hover:border-emerald-300">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <feature.icon className="w-6 h-6 text-emerald-700" />
                  </div>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                    {feature.highlight}
                  </span>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-900 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};